// Login redirects will not work across domains.
// if cake is configured to use staging, you will be routed
// to the staging login page, but you will not be redirected
// to your local cake instance once you've logged in.
//
// See the 'using a real session with Athena staging'
// section in the README for more information.
import {
  AUTH_ERROR_PROVIDER_QUERY_PARAM,
  AUTH_ERROR_QUERY_PARAM,
  EMAIL_PREFILL_QUERY_PARAM,
  LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM,
} from '@opendoor/auth-fe/globals';
import { DOMAIN } from 'shared/helpers/globals';

/**
 * Redirects the user to the login page with the appropriate query params.
 * @param requestUrl - The URL to redirect to after successful login
 */
export const getLoginUrl = (requestUrl: string): string => {
  let redirectUrl = getLoginUrlWithRedirect(requestUrl);
  const searchParams = requestUrl ? new URL(requestUrl).searchParams : new URLSearchParams();
  const accessToken = searchParams.get(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);
  const emailPrefill = searchParams.get(EMAIL_PREFILL_QUERY_PARAM);

  if (accessToken) {
    // If there is no user but there is an `access_token` param in the URL, we will
    // redirect to the login lite url (opendoor.com/token). Login-lite does not lift
    // auth_error: the OAuth callback never emits access_token alongside it.
    redirectUrl = getLoginLiteUrlWithRedirect(requestUrl, accessToken);
  } else if (emailPrefill) {
    // If the user is not authenticated we redirect to the login page.
    // If there is an `emailPrefill` param in the URL, we add it as a query param
    // to pre-populate the log-in/sign-up form
    redirectUrl = getLoginUrlWithRedirect(requestUrl, emailPrefill);
  }
  return redirectUrl;
};

const getRedirectUrl = (url: string) => encodeURIComponent(url);

// The OAuth redirect flow appends auth_error(_provider) to the page the user
// was on. When that page bounces here, the login form's redirect-auth buttons
// read these params from their own top-level query string, so they must be
// lifted out of the nested redirectTo target onto the /login URL itself.
const AUTH_ERROR_PARAMS = [AUTH_ERROR_QUERY_PARAM, AUTH_ERROR_PROVIDER_QUERY_PARAM] as const;

const liftAuthErrorParams = (
  requestUrl: string,
): { redirectTarget: string; authErrorQuery: string } => {
  let target: URL;
  try {
    target = new URL(requestUrl);
  } catch {
    return { redirectTarget: requestUrl, authErrorQuery: '' };
  }

  const lifted = new URLSearchParams();
  for (const param of AUTH_ERROR_PARAMS) {
    const value = target.searchParams.get(param);
    if (value) {
      lifted.set(param, value);
      target.searchParams.delete(param);
    }
  }

  const authErrorQuery = lifted.toString();
  // Only hand back a re-serialized target when we actually removed a param;
  // otherwise return requestUrl verbatim so URL normalization (param reordering,
  // %20 -> +) never mutates the post-login destination.
  return {
    redirectTarget: authErrorQuery ? target.toString() : requestUrl,
    authErrorQuery: authErrorQuery ? `&${authErrorQuery}` : '',
  };
};

/**
 * Returns the URL for normal login. If successfully authenticated, the user will be redirected to the current location.
 *
 * @param env - Environment to use to get the correct domain
 * @param emailPrefill  - [Optional] Email to use to prefill the log-in/sign-up form
 */
const getLoginUrlWithRedirect = (requestUrl: string, emailPrefill?: string) => {
  const emailPrefillParam = emailPrefill ? `&${EMAIL_PREFILL_QUERY_PARAM}=${emailPrefill}` : '';
  const { redirectTarget, authErrorQuery } = liftAuthErrorParams(requestUrl);
  return `${DOMAIN}/login?redirectTo=${getRedirectUrl(redirectTarget)}${emailPrefillParam}${authErrorQuery}`;
};

/**
 * @description
 * Returns the URL for login lite. If successfully authenticated, the user will be redirected to the current location.
 *
 * Domain: `opendoor.com/token`
 * Query params:
 *    1. `access_token=<TOKEN>`
 *    2. `redirect_url=<ENCODED_CURRENT_URL>`
 */
const getLoginLiteUrlWithRedirect = (redirectUrl: string, accessToken: string) => {
  return `${DOMAIN}/token?${LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM}=${accessToken}&redirect_url=${getRedirectUrl(
    redirectUrl,
  )}`;
};
